import React, { useCallback, useEffect, useRef, useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { createStructuredSelector } from "reselect";
import { connect } from "react-redux";

import { change } from "store/root/actions";
import { selectUser } from "store/users/selectors";

import { createStripeSession, updatePaymentTax, checkoutSuccess } from "utils/payment/api";

import Loading from "components/Loading";

import SuccessIcon from "assets/images/icons/checklistComplete.svg";

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLIC_KEY);

const Step2 = ({
    isOpen,
    amountToPurchase,
    currentUser,
    change,
    triggerPay,
    onPaymentFinish,
    setPaymentStatus,
    onTaxChange,
    onClose,
}) => {
    const [clientSecret, setClientSecret] = useState(null);
    const [sessionId, setSessionId] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [onCompleteState, setOnCompleteState] = useState({
        isLoading: false,
        isSuccess: false,
    });

    const { email = "", uid = "" } = currentUser || {};

    // Create Stripe session on step open
    useEffect(() => {
        if (!isOpen) return;

        (async () => {
            try {
                setIsLoading(true);
                setPaymentStatus("preparing");

                const res = await createStripeSession({
                    creditsToPurchase: amountToPurchase,
                    customerEmail: email,
                });

                // DashboardAPI returns the axios error object instead of throwing
                if (!res?.data || res?.isAxiosError) {
                    const message =
                        res?.response?.data?.message || res?.message || "Failed to start checkout session";
                    throw Object.assign(new Error(message), { response: res?.response });
                }

                const { clientSecret: cs, paymentIntentId: sid } = res.data;

                if (!cs || !sid) {
                    throw new Error("Checkout session response was incomplete");
                }

                setClientSecret(cs);
                setSessionId(sid);

                setIsLoading(false);
                setPaymentStatus("idle");
            } catch (err) {
                console.error("Error creating Stripe session:", err);
                setIsLoading(false);
                setPaymentStatus("idle");
                onPaymentFinish?.("error", err?.message || "Failed to start checkout session");
            }
        })();

        return () => {
            setClientSecret(null);
            setSessionId(null);
        };
        // Only (re)create the checkout session when the step opens or the purchase
        // inputs change. setPaymentStatus/onPaymentFinish are callbacks whose identity
        // can change every parent render — including them here caused an infinite loop
        // that minted a new PaymentIntent on every render.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, amountToPurchase, email]);

    const onComplete = useCallback(async () => {
        try {
            setOnCompleteState({ isLoading: true, isSuccess: false });

            const res = await checkoutSuccess({
                paymentIntentId: sessionId,
                userUid: uid,
                creditsToAdd: amountToPurchase,
            });

            // DashboardAPI returns the axios error object instead of throwing
            if (!res?.data || res?.isAxiosError) {
                const message =
                    res?.response?.data?.message ||
                    res?.message ||
                    "An error occurred during payment completion.";
                throw Object.assign(new Error(message), { response: res?.response });
            }

            const { newCredits = 0 } = res.data;
            change("user.creditSummary.credits", newCredits);

            setOnCompleteState({ isLoading: false, isSuccess: true });
            onPaymentFinish("success");

            setTimeout(() => {
                setOnCompleteState({ isLoading: false, isSuccess: false });
                setPaymentStatus("idle");
                onClose();
            }, 5000);
        } catch (error) {
            onPaymentFinish("error", error?.message || "An error occurred during payment completion.");
            setOnCompleteState({ isLoading: false, isSuccess: false });
            setPaymentStatus("idle");
            console.log("ERROR in onComplete", error);
        }
    }, [sessionId, uid, amountToPurchase, change, onPaymentFinish, onClose, setPaymentStatus]);

    if (isLoading || onCompleteState.isLoading) return <Loading />;

    if (!clientSecret) return null;

    return (
        <div style={{ padding: "20px 45px" }}>
            {onCompleteState.isSuccess ? (
                <div style={{ textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center" }}>
                    <img src={SuccessIcon} alt="Success" style={{ width: 100, height: 100 }} />
                    <h2 style={{ marginTop: 20 }}>Payment Successful!</h2>
                    <p>Your credits have been updated.</p>
                </div>
            ) : (
                <Elements stripe={stripePromise} options={{ clientSecret }}>
                    <CheckoutForm
                        paymentIntentId={sessionId}
                        clientSecret={clientSecret}
                        triggerPay={triggerPay}
                        onComplete={onComplete}
                        onPaymentFinish={onPaymentFinish}
                        setPaymentStatus={setPaymentStatus}
                        onTaxChange={onTaxChange}
                    />
                </Elements>
            )}
        </div>
    );
};

const CheckoutForm = ({
    paymentIntentId,
    clientSecret,
    triggerPay,
    onComplete,
    onPaymentFinish,
    setPaymentStatus,
    onTaxChange,
}) => {
    const stripe = useStripe();
    const elements = useElements();

    // ConfirmationToken minted from the PaymentElement. It carries the billing postal code
    // Stripe's own card field collected (no extra field of ours) and is reused to confirm.
    const confirmationTokenRef = useRef(null);
    // Country:postal we last priced — avoids re-hitting the tax API for the same address.
    const lastPricedKeyRef = useRef(null);
    // Debounce so we price once the buyer stops typing the postal, not on every keystroke.
    const debounceRef = useRef(null);

    // Auto-price Stripe Tax as soon as the card form (incl. Stripe's postal field) is
    // complete: mint a ConfirmationToken to read the postal code, update the PI amount
    // server-side, and fill Tax/Total in the footer — no extra field, no extra tap.
    const priceTax = useCallback(async () => {
        setPaymentStatus("pricing");

        const { error: submitError } = await elements.submit();
        if (submitError) {
            setPaymentStatus("idle");
            return;
        }

        const { confirmationToken, error: tokenError } = await stripe.createConfirmationToken({ elements });
        if (tokenError) {
            setPaymentStatus("idle");
            return;
        }

        const address = confirmationToken?.payment_method_preview?.billing_details?.address || {};
        if (!address.postal_code) {
            // No postal collected — can't compute tax; leave the placeholder in place.
            setPaymentStatus("idle");
            return;
        }

        // Refresh the token every price so confirm always uses current card details
        confirmationTokenRef.current = confirmationToken.id;

        const key = `${address.country || ""}:${address.postal_code}`;
        if (key === lastPricedKeyRef.current) {
            // Same address already priced — keep the existing quote, just refreshed the token
            setPaymentStatus("idle");
            return;
        }

        onTaxChange?.({ status: "loading" });

        const res = await updatePaymentTax({
            paymentIntentId,
            address: {
                postal_code: address.postal_code,
                state: address.state,
                country: address.country,
            },
        });

        if (!res?.data || res?.isAxiosError) {
            lastPricedKeyRef.current = null;
            onTaxChange?.({ status: "error" });
            setPaymentStatus("idle");
            return;
        }

        const { subtotal, taxAmount, amountTotal } = res.data;
        lastPricedKeyRef.current = key;
        onTaxChange?.({ status: "ready", subtotal, taxAmount, amountTotal });
        setPaymentStatus("idle");
    }, [stripe, elements, paymentIntentId, onTaxChange, setPaymentStatus]);

    const handleElementChange = useCallback(
        (event) => {
            clearTimeout(debounceRef.current);

            // Form no longer complete (e.g. editing the postal) — invalidate the quote
            if (!event.complete) {
                confirmationTokenRef.current = null;
                lastPricedKeyRef.current = null;
                onTaxChange?.(null);
                return;
            }

            // Complete → price tax shortly after they stop typing
            debounceRef.current = setTimeout(() => {
                priceTax();
            }, 500);
        },
        [priceTax, onTaxChange]
    );

    // The "Pay" tap: confirm the (now tax-inclusive) PaymentIntent with the token we minted.
    useEffect(() => {
        if (!triggerPay || !stripe) return;
        if (!confirmationTokenRef.current) return; // button is disabled until priced, but guard anyway

        (async () => {
            setPaymentStatus("processing");

            const { error, paymentIntent } = await stripe.confirmPayment({
                clientSecret,
                confirmParams: {
                    return_url: window.location.href,
                    confirmation_token: confirmationTokenRef.current,
                },
                redirect: "if_required",
            });

            if (error) {
                console.error("Stripe error:", error.message);
                // Token/amount may be stale — force a fresh quote on retry
                confirmationTokenRef.current = null;
                lastPricedKeyRef.current = null;
                onTaxChange?.(null);
                setPaymentStatus("idle");
                onPaymentFinish("error", error.message);
                return;
            }

            if (paymentIntent?.status === "succeeded") {
                await onComplete();
                onPaymentFinish("success");
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [triggerPay]);

    return <PaymentElement onChange={handleElementChange} />;
};

// Redux wiring
const mapStateToProps = createStructuredSelector({
    currentUser: selectUser,
});

const mapDispatchToProps = (dispatch) => ({
    change: (path, value) => dispatch(change(path, value)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Step2);
